// Siri Shortcuts / App Intents bridge (iOS native only).
// Web/Android paths return no-ops so callers don't need to gate.
//
// Native flow:
//   Siri intent  →  writes pendingAction to App Group UserDefaults  →
//   app foregrounds  →  React calls consumePendingAction()  →  executes
//
// React side also writes prayerTimes + lat/lng + userName into the same
// App Group so the NextPrayerIntent can answer without opening the app.

function isNative() {
  return !!window.Capacitor?.isNativePlatform?.();
}

function getPlugin() {
  return window.Capacitor?.Plugins?.SiratIntents;
}

export async function writePrayerTimes({ date, timings, lat, lng }) {
  if (!isNative()) return { written: false, reason: "not-native" };
  const plugin = getPlugin();
  if (!plugin) return { written: false, reason: "plugin-missing" };
  if (!date || !timings) return { written: false, reason: "missing-args" };
  const times = {
    Fajr: String(timings.Fajr || "").replace(/\s*\(.*\)$/, "").trim(),
    Dhuhr: String(timings.Dhuhr || "").replace(/\s*\(.*\)$/, "").trim(),
    Asr: String(timings.Asr || "").replace(/\s*\(.*\)$/, "").trim(),
    Maghrib: String(timings.Maghrib || "").replace(/\s*\(.*\)$/, "").trim(),
    Isha: String(timings.Isha || "").replace(/\s*\(.*\)$/, "").trim()
  };
  try {
    return await plugin.writePrayerTimes({ date, times, lat: lat || 0, lng: lng || 0 });
  } catch (e) {
    return { written: false, reason: String(e) };
  }
}

export async function writeUserName(name) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try { await plugin.writeUserName({ name: name || "" }); } catch {}
}

export async function consumePendingAction() {
  if (!isNative()) return null;
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const res = await plugin.consumePendingAction();
    return res?.action || null;
  } catch {
    return null;
  }
}

// Subscribe to sirat:// URL opens (Live Activity widgetURL taps).
// AppDelegate posts a NotificationCenter event the plugin forwards via
// notifyListeners("openURL", ...). This is the reliable warm-resume path
// — UserDefaults cross-instance caching is unreliable. Returns an
// unsubscribe function. No-op on web.
export function addOpenURLListener(handler) {
  if (!isNative()) return () => {};
  const plugin = getPlugin();
  if (!plugin || typeof plugin.addListener !== "function") return () => {};
  let h;
  try {
    h = plugin.addListener("openURL", (data) => {
      try { handler(data); } catch {}
    });
  } catch {
    return () => {};
  }
  return () => { try { h?.remove?.(); } catch {} };
}

// Reads prayer completion for a given date directly from the App Group
// UserDefaults key written by both the app and the widget extension.
// Returns { found, Fajr, Dhuhr, Asr, Maghrib, Isha } with 1/0 integers,
// or null on failure. Call on foreground to pick up widget-side changes.
export async function readPrayerCompletion(date) {
  if (!isNative()) return null;
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const res = await plugin.readPrayerCompletion({ date });
    return res?.found ? res : null;
  } catch {
    return null;
  }
}

// Writes today's prayer completion status to App Group UserDefaults so
// the SiratHomeWidgets extension can show ✓ markers without opening the app.
// completionMap = { Fajr: bool, Dhuhr: bool, Asr: bool, Maghrib: bool, Isha: bool }
export async function writePrayerCompletion(completionMap, date) {
  if (!isNative()) return { written: false, reason: "not-native" };
  const plugin = getPlugin();
  if (!plugin) return { written: false, reason: "plugin-missing" };
  if (!date) return { written: false, reason: "missing-date" };
  const completion = {
    fajr:    !!completionMap?.Fajr,
    dhuhr:   !!completionMap?.Dhuhr,
    asr:     !!completionMap?.Asr,
    maghrib: !!completionMap?.Maghrib,
    isha:    !!completionMap?.Isha
  };
  try {
    return await plugin.writePrayerCompletion({ date, completion });
  } catch (e) {
    return { written: false, reason: String(e) };
  }
}

// Writes a 14-day rolling prayer count to App Group UserDefaults so the
// WeeklyPrayerWidget can show Sun-Sat grids without opening the app.
// salahLog = { "YYYY-MM-DD": { Fajr: bool, Dhuhr: bool, ... }, ... }
export async function writeWeeklyLog(salahLog) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  const TRACKABLE = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
  const log = {};
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const ds = `${y}-${m}-${day}`;
    const dayLog = salahLog[ds] || {};
    log[ds] = TRACKABLE.filter(p => !!dayLog[p]).length;
  }
  try { await plugin.writeWeeklyLog({ log }); } catch {}
}

// Writes today's calorie + macro totals to App Group UserDefaults so the
// GhidhaCalorieWidget can display consumed/remaining without opening the app.
export async function writeCalorieData({ date, consumed, goal, protein, proteinGoal, carbs, carbsGoal, fat, fatGoal }) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try {
    await plugin.writeCalorieData({
      date,
      consumed: consumed || 0,
      goal: goal || 2000,
      protein: protein || 0,
      proteinGoal: proteinGoal || 150,
      carbs: carbs || 0,
      carbsGoal: carbsGoal || 225,
      fat: fat || 0,
      fatGoal: fatGoal || 65
    });
  } catch {}
}

// Writes today's Apple Health vitals to App Group UserDefaults so the
// GhidhaVitalsWidget can display steps, active kcal, and sleep without opening the app.
export async function writeVitalsData({ date, steps, caloriesBurned, basalBurned, sleepMinutes, deepMinutes, remMinutes }) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try {
    await plugin.writeVitalsData({
      date,
      steps:          steps          || 0,
      caloriesBurned: caloriesBurned || 0,
      basalBurned:    basalBurned    || 0,
      sleepMinutes:   sleepMinutes   || 0,
      deepMinutes:    deepMinutes    || 0,
      remMinutes:     remMinutes     || 0,
    });
  } catch {}
}

// Writes the top 3 most-frequently-logged meals to App Group UserDefaults so the
// GhidhaCalorieWidget can show interactive quick-log buttons without opening the app.
// meals = [{ name, calories, protein, carbs, fat, servingG, servingLabel }]
export async function writeFrequentMeals(meals) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try { await plugin.writeFrequentMeals({ meals }); } catch {}
}

export async function getDebugSnapshot() {
  const out = {
    native: isNative(),
    pluginPresent: !!getPlugin(),
    pluginNames: Object.keys(window.Capacitor?.Plugins || {}),
    snapshot: null,
    error: null
  };
  if (!out.native || !out.pluginPresent) return out;
  try {
    out.snapshot = await getPlugin().getDebugSnapshot();
  } catch (e) {
    out.error = String(e);
  }
  return out;
}
